import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, UserCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUsers = useCallback(() => {
    api.get('/users').then(r => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      toast.error('E-posta ve şifre gerekli'); return;
    }
    if (form.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı'); return;
    }
    setIsSubmitting(true);
    try {
      await api.post('/users', form);
      toast.success('Kullanıcı oluşturuldu');
      setForm({ name: '', email: '', password: '', role: 'admin' });
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`${email} kullanıcısını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme hatası');
    }
  };

  return (
    <div data-testid="admin-user-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Kullanıcı Yönetimi</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Kullanıcı
        </button>
      </div>

      {/* New User Form */}
      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Yeni Kullanıcı Ekle</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Ad Soyad</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Kullanıcı adı"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">E-posta <span className="text-destructive">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                  placeholder="kullanici@firma.com"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Şifre <span className="text-destructive">*</span></label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                  placeholder="En az 6 karakter"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Rol</label>
                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editör</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50"
                data-testid="admin-save-button"
              >
                {isSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">Henüz kullanıcı yok</div>
      ) : (
        <div className="space-y-3">
          {users.map(user => (
            <div key={user.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-full bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                <UserCircle className="w-5 h-5 text-[hsl(var(--gold))]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{user.name || 'İsimsiz'}</div>
                <div className="text-xs text-muted-foreground">{user.email}</div>
              </div>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))]">
                <Shield className="w-3 h-3" />
                {user.role === 'admin' ? 'Admin' : 'Editör'}
              </span>
              <span className="text-xs text-muted-foreground hidden sm:block">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : ''}
              </span>
              <button
                onClick={() => handleDelete(user.id, user.email)}
                className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"
                title="Kullanıcıyı sil"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
